import './styles.css';

import {
  AbsoluteCenter,
  Box,
  Flex,
  Icon,
  Image,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import i18next from 'i18next';
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BsTools } from 'react-icons/bs';
import { RiUser3Fill } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { COMMON_WEB_PATH } from '../../constant';
import useCheckLogin from '../../hook/useCheckLogin';
import useDeviceType from '../../hook/useDeviceType';
import useSound from '../../hook/useSound';
import { CloseMenu, notice } from '../../redux/action/action';
import {
  clearGameLink,
  startGameLoading,
  storeGameConfig
} from '../../redux/action/game/gameAction';
import {
  buildGameImageCandidates,
  nextGameImageSrc,
  resolveGameImageSrc
} from '../../utils/gameImageSource';
import TooltipWithTouch from '../Common/TooltipWithTouch';
import LazyImage from '../Loading/lazyImage';
import ToggleFavorite from '../Favorite/ToggleFavorite';
import GameTag from './GameTag';
import useIsLandScapeMachine from '@/hook/useIsLandScapeMachine';
import useGames from '@/pages/Game/useGames';

const GameItem = ({
  originProps,
  gameProps,
  specMask,
  forceShow,
  liveLayout,
  customHeight,
  longLayout, //是否使用長版型
  hiddenTag, //是否隱藏遊戲標籤
  specialBorder, //是否使用特殊邊框
  longImage, // 是否使用長圖
  machineLayout,
  isNormalImage,
  centerMode,
  preloadResolve,
  onImageResolved,
  recentedPlayedComponent = <></>
}) => {
  const { t } = useTranslation();
  const i18n_common = (key) => t(`common.${key}`);
  const { gameCode, platform, gameName, gameSubType } = gameProps;

  /* 圖片候選清單(單一真實來源)。
     src、preload probe、onError 三邊都吃這一份 —— 以前是各寫各的規則,
     長圖那條只有 probe 跟 src 有、onError 沒有,一 404 就死在畫面上。
     語言變動時要重算,所以把 i18next.language 放進依賴。 */
  const { language: currentLang } = i18next;
  const imageCandidates = useMemo(
    () =>
      buildGameImageCandidates({
        platform,
        gameCode,
        longImage: Boolean(longLayout && longImage),
        lang: currentLang
      }),
    [platform, gameCode, longLayout, longImage, currentLang]
  );

  /* B(滑軌 loop clone 修正):先探測出「存在的圖片」再顯示，clone 才不會凍住 404 的 src。
     只在 preloadResolve=true(滑軌)啟用，一般遊戲格不受影響。 */
  const [resolvedSrc, setResolvedSrc] = useState(null);
  useEffect(() => {
    if (!preloadResolve) return;
    let cancelled = false;
    resolveGameImageSrc(imageCandidates).then((url) => {
      if (cancelled) return;
      if (url) setResolvedSrc(url);
      /* 即使全部候選都失敗也要通知一次,否則 Splide 的 scheduleRefresh
         永遠不會跑,整條滑軌會卡在等一個不會到的事件。 */
      onImageResolved?.();
    });
    return () => {
      cancelled = true;
    };
    // onImageResolved 由父層每次 render 重建,放進依賴會讓探測無限重跑
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadResolve, imageCandidates]);
  //遊戲ITEM的資料
  const { gameNameEn, game_tags, game_subtype } = originProps;
  const isMobile = useDeviceType() === 'Mobile';

  const { isLobby, isLiveLayoutOpen, showLogoInCenter } = liveLayout || {};

  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);
  const onlinePlayer = useSelector((state) => state.isGameOnlinePlayer);
  const { game } = onlinePlayer || {};

  const textColor = useColorModeValue('dark.100', 'light.100');
  const itemBg = useColorModeValue('light.100', 'dark.250');
  const textBgColor = useColorModeValue('light.200', 'dark.100');

  const { isLogin, isMachineUser } = useCheckLogin();

  const playButtonSound = useSound(); // 進遊戲按鈕音效
  const hoverButtonSound = useSound({
    customSound: 'slide_sound',
    volume: 0.3
  });

  const { allGamePlatform = [] } = isHandleAllGame;

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const getPrevPath = useMemo(() => {
    const path = location.pathname.split('/');
    const currentPathIndex = path.findIndex((item) => item === 'game-confirm');

    if (currentPathIndex === -1) return '';

    return `${path.slice(0, currentPathIndex).join('/')}/`;
  }, [location.pathname]);

  const currentPlayer = useMemo(() => {
    return game?.[originProps.gameUid] || 0;
  }, [game]);

  const isLandscapeMachine = useIsLandScapeMachine();

  const getPlatformIsMaintain = useMemo(() => {
    const findPlatform = allGamePlatform.find(
      (item) => item.platform === platform
    );
    /**
     * @param maintenanceStartTime {string}  維護開始時間
     * @param maintenanceEndTime {string}  維護結束時間
     * @param temp_maintenance_start_time {string} 暫時維護開始時間
     * @param temp_maintenance_end_time {string} 暫時維護結束
     * NOTE:會優先用暫時維護時間
     *       如果暫時維護時間不存在，則使用正式維護時間
     * @param isUnderMaintenance {boolean} 是否維護中
     */
    const {
      maintenanceStartTime,
      maintenanceEndTime,
      temp_maintenance_start_time,
      temp_maintenance_end_time
    } = findPlatform || {};
    const isMaintain = findPlatform?.isUnderMaintenance;
    if (isMaintain) {
      return {
        state: true,
        component: (
          <Box
            position={'absolute'}
            top="50%"
            left="50%"
            transform="translate(-50%,-50%)"
            bg="rgba(0,0,0,0.7)"
            w="100%"
            h={'100%'}
            zIndex="99"
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection={'column'}
            gap="10px"
            color="white"
          >
            <TooltipWithTouch
              boxClassName={{
                w: '100%',
                h: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column'
              }}
              label={
                <Flex
                  direction={'column'}
                  justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Text as="span">
                    {temp_maintenance_start_time || maintenanceStartTime}
                  </Text>
                  <Text as="span">~</Text>
                  <Text as="span">
                    {temp_maintenance_end_time || maintenanceEndTime}
                  </Text>
                </Flex>
              }
            >
              <Icon as={BsTools} fontSize="30px" />
              <Flex
                direction={'column'}
                alignItems={'center'}
                justifyContent={'center'}
                fontSize="sm"
                fontWeight={'500'}
              >
                <Text as="span">{i18n_common('platformMaintain')}</Text>
              </Flex>
            </TooltipWithTouch>
          </Box>
        )
      };
    }
  }, [platform, longLayout, machineLayout]);

  const { handlePlayGame } = useGames();

  const handleEnterGame = () => {
    // 播放按鈕音效
    playButtonSound();
    if (getPlatformIsMaintain?.state) return;
    dispatch(storeGameConfig(originProps));
    dispatch(clearGameLink());
    dispatch(CloseMenu());
    dispatch(startGameLoading());

    const game_name = gameNameEn.replace(/ /g, '-');
    const game_platform = originProps.game_platform.replace(/ /g, '-');

    handlePlayGame({
      gameName: game_name,
      gamePlatform: game_platform,
      gameUid: originProps.gameUid
    });
  };
  return (
    <Flex
      role="group"
      direction={`column`}
      position={`relative`}
      cursor={
        getPlatformIsMaintain?.state && !forceShow ? `not-allowed` : `pointer`
      }
      onClick={handleEnterGame}
      onMouseEnter={isMobile ? () => false : hoverButtonSound}
      overflow="hidden"
      my="2px"
      h="100%"
      hover={{
        background: {
          md: getPlatformIsMaintain?.state
            ? `none`
            : specMask
              ? `rgba(0,0,0,0.1)`
              : `brand.500`
        }
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="0" height="0">
        <defs>
          <clipPath id="trapezoid" clipPathUnits="objectBoundingBox">
            <path
              d="
        M 0.191 0.991
        L 0.807 0.992
        C 0.864 0.989 0.921 0.964 0.933 0.898
        L 0.933 0.899
        L 0.988 0.103
        C 0.985 0.000 0.878 0.000 0.845 0.000
        L 0.113 0.001
        C 0.070 0.000 0.000 0.052 0.007 0.088
        L 0.061 0.889
        C 0.054 0.927 0.116 0.991 0.191 0.988
        Z
      "
            />
          </clipPath>
        </defs>
      </svg>
      {/* 加入最愛(參考 381):置右上角圓形底,登入才顯示;外框 stopPropagation 避免誤觸進遊戲 */}
      {isLogin && (
        <Flex
          position="absolute"
          top="0.55em"
          right="0.55em"
          zIndex={20}
          w="2em"
          h="2em"
          align="center"
          justify="center"
          borderRadius="full"
          bg="rgba(0,0,0,0.5)"
          border="1.5px solid rgba(255,255,255,0.55)"
          boxShadow="0 2px 6px rgba(0,0,0,0.45)"
          backdropFilter="blur(2px)"
          cursor="pointer"
          lineHeight="1"
          transition="transform 0.15s ease, background 0.15s ease"
          _hover={{ transform: 'scale(1.12)', bg: 'rgba(0,0,0,0.65)' }}
          _active={{ transform: 'scale(0.9)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <ToggleFavorite
            fontSize="1.05em"
            customGameUid={originProps.gameUid}
          />
        </Flex>
      )}
      {/* {hiddenTag ? null : (
        <GameTag
          isLongLayout={longLayout || machineLayout}
          game_tags_props={game_tags || []}
        />
      )} */}
      <Box
        position={'absolute'}
        w="100%"
        h="100%"
        top="0px"
        left="0px"
        bgImage={`url('${COMMON_WEB_PATH}/blue-layout/gameType/${machineLayout && centerMode ? 'game_item_background_border' : 'game_item_mini_border'}.png')`}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="100%100%"
        zIndex={4}
        onClick={handleEnterGame}
        cursor={'pointer'}
        display={'flex'}
        alignItems={'flex-end'}
        justifyContent={'center'}
        pb="2vw"
      ></Box>{' '}
      <Box
        p={1}
        bgImage={`url('${COMMON_WEB_PATH}/blue-layout/gameType/${machineLayout && centerMode ? 'game_item_background_mask' : 'game_item_mini_mask_background'}.png')`}
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="100%100%"
        h="100%"
        borderRadius={machineLayout && centerMode ? '0' : '20%'}
        overflow="hidden"
        position="relative"
      >
        <Box className={machineLayout && centerMode ? 'shape-radius' : ''}>
          {getPlatformIsMaintain?.component}
          <Box
            pt={
              machineLayout
                ? {
                    base: isLandscapeMachine ? '24vh' : '46vh'
                  }
                : customHeight
                  ? {
                      base: '162%',
                      md: '162%'
                    }
                  : {
                      base: '97%',
                      md: '100%'
                    }
            }
            position="relative"
            overflow="hidden"
            h="100%"
          >
            <Box
              zIndex={2}
              position="absolute"
              top="0px"
              left="0px"
              height="100%"
              width="100%"
              // overflow="hidden"
              isolation={`isolate`}
            >
              <Box
                height={'100%'}
                width={'100%'}
                px={longLayout || machineLayout ? '3px' : '3px'}
              >
                <Box h="100%">
                  <LazyImage
                    /* 候選順序見 utils/gameImageSource:
                       長圖 en/_long.webp → en/_long.gif → 再退回一般方圖的四段鏈。
                       滑軌(preloadResolve)已經探測出存在的圖就直接用探測結果。 */
                    src={
                      preloadResolve && resolvedSrc
                        ? resolvedSrc
                        : imageCandidates[0]
                    }
                    alt={gameNameEn}
                    closeInverted
                    isNormalImage={isNormalImage}
                    className={`cut-corners-mask h-full w-full`}
                    onError={(e) => {
                      /* 用「目前 src 在候選清單的位置 + 1」推進,不再比對路徑尾巴。
                         舊寫法是一串 endsWith,長圖的路徑一個都 match 不到,
                         等於長圖完全沒有 fallback。 */
                      const img = e.target;
                      const next = nextGameImageSrc(img.src, imageCandidates);
                      if (next) {
                        img.src = next;
                      } else {
                        img.onerror = null; // 全部試完,停手避免無限迴圈
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box
                position={`absolute`}
                top="0%"
                left="0%"
                height={'100%'}
                width={'100%'}
                display={{
                  base: 'none',
                  md: 'flex'
                }}
                alignItems={`flex-end`}
                justifyContent={`center`}
                opacity={0}
                _groupHover={{
                  opacity: 1,
                  background: 'rgba(0,0,0,0.5)'
                }}
                bgRepeat={`no-repeat`}
                bgSize="100% 100%"
                bgPos="center"
              >
                <Image
                  onClick={handleEnterGame}
                  cursor={'pointer'}
                  src={`${COMMON_WEB_PATH}/blue-layout/gameType/play_button.png`}
                />
              </Box>
              {recentedPlayedComponent}
            </Box>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default React.memo(GameItem);
